import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/swiper-react.cjs';
import SwiperCore, { Autoplay, EffectFade, Navigation, Pagination } from 'swiper';
import {
    getPublicImagePath,
    getUploadedImagePath,
    useAppDispatch,
    useAppState,
    useBreakpoints
} from '@nutastic/nutastic-react-app';
import HighlightBox from '../HighlightBox';
import { BarsOutlined } from '@ant-design/icons';
import { useNavigation } from '../../hooks/useNavigation';
import { BANNER_SWIPER_PROPS, CONSTANTS, COVER_SWIPER_PROPS, ONE_HOUR_MILLI } from '../../utils/constants';
import Footer from '../Footer';
import classnames from 'classnames';
import { rehydrateState } from '../../state/actions.mjs';
import LoadingOverlay from '../LoadingOverlay';

SwiperCore.use([Navigation, Autoplay, EffectFade, Pagination]);
const componentClass = 'home-page';
//TODO-NEXT: make opacity 0 for main when sub page is opened...fade out should happen and fade back in when turned off. Lock scroll too

const HomePage = () => {
    const appDispatch = useAppDispatch();
    const { pages: { homePage }, isReloadingAppData } = useAppState();
    const { lg: isLargeScreen } = useBreakpoints();
    const [lastReload, setLastReload] = useState(Date.now());
    const isReadyToRender = isLargeScreen !== undefined;
    const { slides, sliderSections, sliderSectionsBottom, welcomeMessage, highlight1, highlight2, highlight3, highlight4, highlight5 } = homePage;
    const { loadPage } = useNavigation();

    const onSwiperSlide = () => {
        const rightNow = Date.now();
        if (rightNow - lastReload >= (ONE_HOUR_MILLI / 2)) {
            setLastReload(rightNow);
            appDispatch( rehydrateState() );
        }
    };

    const renderSliderSections = (sliderSectionData, max) => {
        const sliders = (sliderSectionData || []).filter(s => s.hasOwnProperty('enableField') && s.enableField !== 'false').map((section, i) => {
            const { title, description, sliderItems, type, url } = section;
            const typeIsSquare = type === CONSTANTS.APPKEY_MUSIC || type === CONSTANTS.APPKEY_SHOP;
            const slidesPerView = (typeIsSquare && isLargeScreen) ? 5.25
                : isLargeScreen ? 3.5
                    : typeIsSquare ? 2.25 : 1.75;
            return <section className="featuredSection">
                <h2>{ title }</h2>
                <p>{ description }</p>
                { url && <div className="sectionActionButton" onClick={() => loadPage(url)}><BarsOutlined /></div> }
                <Swiper className="featuredSection__slider" { ...COVER_SWIPER_PROPS } slidesPerView={slidesPerView}>
                    {
                        sliderItems.map((item, i) => {
                            const { slug, image, title, subText } = item;
                            const urlToLoad = type === CONSTANTS.APPKEY_SHOP ? `/shop/${slug}` : type === CONSTANTS.APPKEY_SERIES ? `/series/${slug}` : `/content/${type}/${slug}`;
                            const itemClasses = classnames('featureItem', {
                                shoppaStyle: type === CONSTANTS.APPKEY_SHOP
                            });
                            return <SwiperSlide key={i}>
                                <div className={itemClasses} onClick={() => loadPage(urlToLoad)}>
                                    <img src={getUploadedImagePath(image)} alt={title}/>
                                    <div>
                                        <p>{ title }</p>
                                        { subText && <i>{ subText }</i> }
                                    </div>
                                </div>
                            </SwiperSlide>
                        })
                    }
                </Swiper>
            </section>;
        });
        if (sliders.length > max) sliders.length = max;
        return sliders;
    };

    return <main className={componentClass} id="nuStationHomeBase">
        <div className="bannerSlideshow">
            { isReadyToRender && <Swiper className="banners" { ...BANNER_SWIPER_PROPS } slidesPerView={ isLargeScreen ? 2.5 : 1.25 } onSlideChange={onSwiperSlide}>
                {
                    (slides || []).map( (slide, i) => {
                        const { image, title, text, urlType, url } = slide;
                        const onBannerClick = () => {
                            if(urlType === 'external') {
                                window.open(url, '_blank').focus();
                            } else if(url) {
                                loadPage(url)
                            } else {
                                console.warn(`Invalid url configured for banner ${i}`);
                            }
                        };

                        return <SwiperSlide>
                            <div className="banner" onClick={onBannerClick}>
                                <img src={ getUploadedImagePath(image) } alt={title} />
                                <div>
                                    <h4>{ title }</h4>
                                    <p>{ text }</p>
                                </div>
                            </div>
                        </SwiperSlide>
                    })
                }
            </Swiper> }
        </div>

        { renderSliderSections(sliderSections, 7) }

        <div className="highlightsSection" >
            <img src={getPublicImagePath('nu-emblem-circle.png')} alt="Nutastic Emblem | Circle Stamp" />
            <p>{ welcomeMessage }</p>
            <div className="highlights">
                <HighlightBox url={highlight1?.url} image={highlight1?.image} title={highlight1?.title} isExternalUrl={highlight1?.urlType === 'external'} />
                <div className="highlights__box">
                    <HighlightBox url={highlight2?.url} isSmall={true} image={highlight2?.image} title={highlight2?.title} isExternalUrl={highlight2?.urlType === 'external'} />
                    <HighlightBox url={highlight3?.url} isSmall={true} image={highlight3?.image} title={highlight3?.title} isExternalUrl={highlight3?.urlType === 'external'} />
                </div>
                <HighlightBox url={highlight4?.url} image={highlight4?.image} title={highlight4?.title} isExternalUrl={highlight4?.urlType === 'external'} />
                <HighlightBox url={highlight5?.url} image={highlight5?.image} title={highlight5?.title} isExternalUrl={highlight5?.urlType === 'external'} />
            </div>
            <span className="fadedFooter" />
        </div>

        { renderSliderSections(sliderSectionsBottom, 5) }

        <Footer />
        { isReloadingAppData && <LoadingOverlay message="Refreshing The Station..." className="appDataReload" /> }
    </main>;
};

export default HomePage;
